import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, Image as ImageIcon, X, CheckCircle,
  AlertTriangle, Loader, ArrowRight, Trophy, Trash2
} from 'lucide-react';
import { projectsAPI } from '../lib/api';
import { PageHeader, LoadingPage, Alert } from '../components/shared';
import toast from 'react-hot-toast';

const LOCKED_STATUSES = ['under_review', 'completed'];

export default function EnterFinalePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [deckFile, setDeckFile] = useState(null);
  const [freeTitle, setFreeTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [removingPath, setRemovingPath] = useState(null);

  const deckInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    projectsAPI.getMyEntryOptions()
      .then(r => {
        const opts = r.data.options || [];
        setOptions(opts);
        if (opts.length === 1) setSelected(opts[0]);
        else if (opts.length > 0 && opts.every(o => o.entry_type === 'freestanding')) setSelected(opts[0]);
      })
      .catch(() => toast.error('Failed to load your entry options.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const refreshSelected = () => {
    projectsAPI.getMyEntryOptions().then(r => {
      const opts = r.data.options || [];
      setOptions(opts);
      const match = opts.find(o => o.entry_type === selected.entry_type && o.entry_id === selected.entry_id);
      if (match) setSelected(match);
    }).catch(() => {});
  };

  const handleDeckSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setDeckFile(f);
  };

  const handleUploadDeck = async () => {
    if (!deckFile) { toast.error('Please choose a file first.'); return; }
    setUploading(true);
    try {
      await projectsAPI.enterFinale(selected.entry_type, selected.entry_id, deckFile, freeTitle);
      toast.success('You are entered for the finale!');
      setDeckFile(null);
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const tooLarge = files.filter(f => f.size > 5 * 1024 * 1024);
    if (tooLarge.length) {
      toast.error(`${tooLarge.length} image(s) exceed 5MB and were skipped.`);
    }
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    if (valid.length) setImageFiles(valid);
  };

  const handleUploadImages = async () => {
    if (!imageFiles.length) return;
    setUploadingImages(true);
    try {
      await projectsAPI.addEntryImages(selected.project_id, imageFiles);
      toast.success('Images added.');
      setImageFiles([]);
      if (imageInputRef.current) imageInputRef.current.value = '';
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add images.');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = async (path) => {
    setRemovingPath(path);
    try {
      await projectsAPI.removeEntryImage(selected.project_id, path);
      toast.success('Image removed.');
      refreshSelected();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove image.');
    } finally {
      setRemovingPath(null);
    }
  };

  if (loading) return <LoadingPage />;

  if (options.length === 0) {
    return (
      <div>
        <PageHeader eyebrow="Grand Finale" title="Enter the Finale" subtitle="Upload your entry deck and you're in." />
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <Trophy size={36} style={{ opacity: 0.3, margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>
            No eligible topic or independent project found for this season.
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Apply to a research topic or submit an independent project first.
          </p>
        </div>
      </div>
    );
  }

  if (!selected) {
    return (
      <div>
        <PageHeader eyebrow="Grand Finale" title="Enter the Finale" subtitle="Choose which project you're entering." />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {options.map(opt => (
            <button
              key={opt.entry_type + opt.entry_id}
              className="card"
              onClick={() => setSelected(opt)}
              style={{
                textAlign: 'left', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.08)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 20,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '1rem' }}>{opt.title}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  {opt.entry_type === 'topic' ? 'Sponsored / Lecturer Topic' : 'Independent Project'}
                  {opt.project_status && <> · Status: {opt.project_status}</>}
                </div>
              </div>
              <ArrowRight size={18} color="var(--text-muted)" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  const isLocked = selected.project_status && LOCKED_STATUSES.includes(selected.project_status);
  const hasEntered = !!selected.entry_deck_path;
  const galleryCount = (selected.gallery_image_paths || []).length;

  return (
    <div>
      <PageHeader
        eyebrow="Grand Finale"
        title="Enter the Finale"
        subtitle={selected.title}
      />

      {options.length > 1 && (
        <button className="btn btn-ghost btn-sm" onClick={() => setSelected(null)} style={{ marginBottom: 20 }}>
          ← Choose a different project
        </button>
      )}

      {isLocked && (
        <div style={{ marginBottom: 24 }}>
          <Alert type="warning">
            This entry has already been submitted for judging and can no longer be changed here.
            Contact admin if you need to make changes.
          </Alert>
        </div>
      )}

      {!isLocked && hasEntered && (
        <div style={{ marginBottom: 24 }}>
          <Alert type="success">
            You're entered for the finale. You can replace your deck or update your image gallery below.
          </Alert>
        </div>
      )}

      {/* Freestanding title input */}
      {selected.entry_type === 'freestanding' && !selected.project_id && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 16 }}>
            Project Title
          </h2>
          <input
            type="text"
            className="form-input"
            placeholder="Enter your project title"
            value={freeTitle}
            onChange={e => setFreeTitle(e.target.value)}
            disabled={isLocked}
            style={{ width: '100%' }}
          />
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 8 }}>
            A domain will be assigned to your project by the admin after submission.
          </p>
        </div>
      )}

      {/* Entry Deck */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500, marginBottom: 16 }}>
          Entry Deck
        </h2>

        {hasEntered && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
            background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.2)',
            borderRadius: 'var(--radius-md)', marginBottom: 16,
          }}>
            <FileText size={18} color="var(--teal)" />
            <a href={selected.entry_deck_path} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--teal)', fontSize: '0.875rem', textDecoration: 'underline' }}>
              View current entry deck
            </a>
          </div>
        )}

        {!isLocked && (
          <>
            <input
              ref={deckInputRef}
              type="file"
              accept=".ppt,.pptx"
              style={{ display: 'none' }}
              onChange={handleDeckSelect}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => deckInputRef.current?.click()}>
                <Upload size={15} /> {deckFile ? deckFile.name : (hasEntered ? 'Choose a new deck' : 'Choose your deck (.pptx)')}
              </button>
              <button
                className="btn btn-primary"
                disabled={!deckFile || uploading || (selected.entry_type === 'freestanding' && !selected.project_id && !freeTitle.trim())}
                onClick={handleUploadDeck}
              >
                {uploading ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</>
                  : (hasEntered ? 'Replace Deck' : 'Upload & Enter the Finale')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Image Gallery */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 500 }}>
            Project Images <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.85rem' }}>({galleryCount}/8)</span>
          </h2>
        </div>

        {galleryCount > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, marginBottom: 16 }}>
            {selected.gallery_image_paths.map(path => (
              <div key={path} style={{ position: 'relative', aspectRatio: '1', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)' }}>
                <img src={path} alt="Project" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                {!isLocked && (
                  <button
                    onClick={() => handleRemoveImage(path)}
                    disabled={removingPath === path}
                    style={{
                      position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.7)', border: 'none', color: '#fff', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {removingPath === path ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <X size={12} />}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!isLocked && galleryCount < 8 && (
          <>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => imageInputRef.current?.click()}>
                <ImageIcon size={14} /> {imageFiles.length ? `${imageFiles.length} image(s) selected` : 'Choose images (max 5MB each)'}
              </button>
              {imageFiles.length > 0 && (
                <button className="btn btn-primary btn-sm" disabled={uploadingImages} onClick={handleUploadImages}>
                  {uploadingImages ? 'Adding…' : 'Add to Gallery'}
                </button>
              )}
            </div>
          </>
        )}

        {galleryCount === 0 && isLocked && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No images added.</p>
        )}
      </div>
    </div>
  );
}
